/**
 * Pool Assignment Service - Pool assignment management for Buraxılış exams
 *
 * Handles creation, management, and querying of pool assignments.
 * Pool assignments allow multiple teachers to grade the same set of students.
 * Only works with Buraxılış (graduation) exams.
 */

import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { YoklaAssignment, PoolAssignmentConfig, Teacher, PoolTeacher } from '../types';
import { FOREIGN_LANGUAGES, normalizeXdilToFull } from '../constants/buraxilisConfig';

/**
 * Result of pool assignment creation
 */
export interface CreatePoolResult {
  success: boolean;
  message: string;
  assignmentId?: string;
}

/**
 * Create pool assignment for Buraxılış exam
 * Admin selects: sinif, fenn, variant → and selects teachers
 *
 * @param fenn - Subject name (e.g., "Riyaziyyat")
 * @param sinif - Grade/class (e.g., "11")
 * @param variant - Exam variant (A, B, C, or D)
 * @param questionNumbers - Array of question numbers (e.g., ["Q79", "Q80"])
 * @param collectionName - The scanned data collection name
 * @param buildingName - Building filter ("Hamısı" for all)
 * @param selectedTeacherIds - Array of teacher IDs to include in pool
 * @param createdBy - Admin ID who created the assignment
 * @returns Creation result with assignment ID if successful
 */
export const createPoolAssignment = async (
  fenn: string,
  sinif: string,
  variant: string,
  questionNumbers: string[],
  collectionName: string,
  buildingName: string,
  selectedTeacherIds: string[],
  createdBy: string
): Promise<CreatePoolResult> => {
  try {
    console.log('🏊 Creating pool assignment:', { fenn, sinif, variant, questionNumbers });

    // 1. Validate: MUST be Buraxılış (enforced by UI, but double-check)
    const examType = 'buraxilis';

    // 2. Check for duplicate pool assignment (same sinif, variant, fenn)
    const existingPoolQuery = query(
      collection(db, 'yoxla-s'),
      where('assignmentType', '==', 'pool'),
      where('examType', '==', 'buraxilis'),
      where('sinif', '==', sinif),
      where('variant', '==', variant),
      where('fenn', '==', fenn),
      where('status', '==', 'active')
    );

    const existingPools = await getDocs(existingPoolQuery);

    if (!existingPools.empty) {
      return {
        success: false,
        message: `Bu fənn üçün artıq hovuz ataması mövcuddur: Sinif ${sinif}, Variant ${variant}, Fənn ${fenn}`
      };
    }

    // 3. Get teacher details
    const teachersQuery = query(
      collection(db, 'users'),
      where('status', '==', 'teacher')
    );
    const teachersSnapshot = await getDocs(teachersQuery);

    const selectedTeachers: PoolTeacher[] = [];

    teachersSnapshot.forEach(teacherDoc => {
      const teacherData = teacherDoc.data() as Teacher;
      if (selectedTeacherIds.includes(teacherDoc.id)) {
        selectedTeachers.push({
          id: teacherDoc.id,
          name: teacherData.name,
          fenn: teacherData.fenn,
          isActive: true // All start as active
        });
      }
    });

    if (selectedTeachers.length === 0) {
      return {
        success: false,
        message: 'Heç bir müəllim seçilməyib'
      };
    }

    // 4. Count students for this sinif+variant
    let studentsQuery = query(
      collection(db, collectionName),
      where('grade', '==', Number(sinif)),
      where('variant', '==', variant)
    );

    // Note: building filter would need compound index if applied here
    // For now, we get all and filter in memory if needed
    const studentsSnapshot = await getDocs(studentsQuery);
    let totalStudents = studentsSnapshot.size;

    // Xarici dil fənnində yalnız həmin xdil-i olan şagirdləri say (Fransız dili = yalnız xdil Fransız olanlar)
    const fennXdilNorm = normalizeXdilToFull(fenn);
    if (FOREIGN_LANGUAGES.includes(fennXdilNorm)) {
      totalStudents = 0;
      studentsSnapshot.forEach(doc => {
        const data = doc.data();
        if (normalizeXdilToFull(data.xdil) === fennXdilNorm) {
          totalStudents++;
        }
      });
    }

    // Apply building filter in memory if specified
    if (buildingName && buildingName !== 'Hamısı') {
      let filteredCount = 0;
      studentsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.buildingName !== buildingName) return;
        if (FOREIGN_LANGUAGES.includes(fennXdilNorm)) {
          if (normalizeXdilToFull(data.xdil) === fennXdilNorm) filteredCount++;
        } else {
          filteredCount++;
        }
      });
      totalStudents = filteredCount;
    }

    // 5. Create pool config
    const poolConfig: PoolAssignmentConfig = {
      teacherIds: selectedTeacherIds,
      teachers: selectedTeachers,
      createdBy,
      createdAt: Timestamp.now()
    };

    // 6. Create initial question progress
    const questionProgress: { [questionNum: string]: { completed: number; total: number } } = {};
    questionNumbers.forEach(qNum => {
      questionProgress[qNum] = { completed: 0, total: totalStudents };
    });

    // 7. Create assignment document
    const assignmentData = {
      hocaId: 'pool', // Special identifier for pool assignments
      hocaName: 'Hovuz',
      fenn,
      sinif,
      variant,
      qrup: variant, // Backward compatibility
      questionNumbers,
      collectionName,
      examType,
      buildingName: buildingName || 'Hamısı',
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedCount: 0,
      totalCount: totalStudents,
      assignmentType: 'pool',
      poolConfig,
      questionProgress
    };

    const docRef = await addDoc(collection(db, 'yoxla-s'), assignmentData);

    console.log('✅ Pool assignment created:', docRef.id);

    return {
      success: true,
      message: `Hovuz ataması uğurla yaradıldı (${selectedTeachers.length} müəllim, ${questionNumbers.length} sual, ${totalStudents} tələbə)`,
      assignmentId: docRef.id
    };
  } catch (error) {
    console.error('❌ Pool assignment creation error:', error);
    return {
      success: false,
      message: 'Hovuz ataması yaradılarkən xəta baş verdi'
    };
  }
};

/**
 * Toggle teacher active status in pool (freeze/unfreeze)
 * Admin can disable teacher access without deleting assignment
 *
 * @param assignmentId - The pool assignment ID
 * @param teacherId - The teacher ID to toggle
 * @param isActive - New active status (true = active, false = frozen)
 * @returns true if successful, false otherwise
 */
export const toggleTeacherInPool = async (
  assignmentId: string,
  teacherId: string,
  isActive: boolean
): Promise<boolean> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);

    if (!assignmentDoc.exists()) {
      console.error('❌ Assignment not found:', assignmentId);
      return false;
    }

    const data = assignmentDoc.data() as YoklaAssignment;

    if (!data.poolConfig) {
      console.error('❌ Not a pool assignment:', assignmentId);
      return false;
    }

    // Update teacher status in poolConfig
    const updatedTeachers = data.poolConfig.teachers.map(t =>
      t.id === teacherId ? { ...t, isActive } : t
    );

    await updateDoc(assignmentRef, {
      'poolConfig.teachers': updatedTeachers,
      updatedAt: Timestamp.now()
    });

    console.log(`${isActive ? '✅ Activated' : '🔒 Frozen'} teacher ${teacherId} in pool ${assignmentId}`);

    return true;
  } catch (error) {
    console.error('❌ Toggle teacher error:', error);
    return false;
  }
};

/**
 * Add a teacher to an existing pool assignment
 *
 * @param assignmentId - The pool assignment ID
 * @param teacherId - The teacher ID to add
 * @returns true if successful, false otherwise
 */
export const addTeacherToPool = async (
  assignmentId: string,
  teacherId: string
): Promise<boolean> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);

    if (!assignmentDoc.exists()) {
      return false;
    }

    const data = assignmentDoc.data() as YoklaAssignment;

    if (!data.poolConfig) {
      return false;
    }

    // Check if teacher already in pool
    if (data.poolConfig.teacherIds.includes(teacherId)) {
      console.log('Teacher already in pool');
      return false;
    }

    // Get teacher details
    const teacherDoc = await getDoc(doc(db, 'users', teacherId));
    if (!teacherDoc.exists()) {
      return false;
    }

    const teacherData = teacherDoc.data() as Teacher;

    // Add teacher to pool
    const newTeacher: PoolTeacher = {
      id: teacherId,
      name: teacherData.name,
      fenn: teacherData.fenn,
      isActive: true
    };

    await updateDoc(assignmentRef, {
      'poolConfig.teacherIds': [...data.poolConfig.teacherIds, teacherId],
      'poolConfig.teachers': [...data.poolConfig.teachers, newTeacher],
      updatedAt: Timestamp.now()
    });

    console.log('✅ Added teacher to pool:', teacherId);
    return true;
  } catch (error) {
    console.error('❌ Add teacher to pool error:', error);
    return false;
  }
};

/**
 * Remove a teacher from a pool assignment
 *
 * @param assignmentId - The pool assignment ID
 * @param teacherId - The teacher ID to remove
 * @returns true if successful, false otherwise
 */
export const removeTeacherFromPool = async (
  assignmentId: string,
  teacherId: string
): Promise<boolean> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);

    if (!assignmentDoc.exists()) {
      return false;
    }

    const data = assignmentDoc.data() as YoklaAssignment;

    if (!data.poolConfig) {
      return false;
    }

    // Remove teacher from pool
    const updatedTeacherIds = data.poolConfig.teacherIds.filter(id => id !== teacherId);
    const updatedTeachers = data.poolConfig.teachers.filter(t => t.id !== teacherId);

    // Must have at least one teacher
    if (updatedTeachers.length === 0) {
      console.error('Cannot remove last teacher from pool');
      return false;
    }

    await updateDoc(assignmentRef, {
      'poolConfig.teacherIds': updatedTeacherIds,
      'poolConfig.teachers': updatedTeachers,
      updatedAt: Timestamp.now()
    });

    console.log('✅ Removed teacher from pool:', teacherId);
    return true;
  } catch (error) {
    console.error('❌ Remove teacher from pool error:', error);
    return false;
  }
};

/**
 * Get pool assignments for a specific teacher
 * Only returns assignments where teacher is in poolConfig.teacherIds AND isActive
 *
 * @param teacherId - The teacher's ID
 * @returns Array of pool assignments the teacher has access to
 */
export const getPoolAssignmentsForTeacher = async (teacherId: string): Promise<YoklaAssignment[]> => {
  try {
    // Query all active pool assignments
    const poolQuery = query(
      collection(db, 'yoxla-s'),
      where('assignmentType', '==', 'pool'),
      where('status', '==', 'active')
    );

    const poolSnapshot = await getDocs(poolQuery);
    const assignments: YoklaAssignment[] = [];

    poolSnapshot.forEach(assignmentDoc => {
      const data = assignmentDoc.data();

      // Check if teacher is in pool and is active
      if (data.poolConfig) {
        const teacherInPool = data.poolConfig.teachers.find(
          (t: PoolTeacher) => t.id === teacherId
        );

        if (teacherInPool && teacherInPool.isActive) {
          assignments.push({
            id: assignmentDoc.id,
            ...data
          } as YoklaAssignment);
        }
      }
    });

    console.log(`📊 Found ${assignments.length} pool assignments for teacher ${teacherId}`);

    return assignments;
  } catch (error) {
    console.error('❌ Get pool assignments error:', error);
    return [];
  }
};

/**
 * Get all pool assignments (for admin view)
 *
 * @returns Array of all pool assignments
 */
export const getAllPoolAssignments = async (): Promise<YoklaAssignment[]> => {
  try {
    const poolQuery = query(
      collection(db, 'yoxla-s'),
      where('assignmentType', '==', 'pool')
    );

    const poolSnapshot = await getDocs(poolQuery);
    const assignments: YoklaAssignment[] = [];

    poolSnapshot.forEach(assignmentDoc => {
      assignments.push({
        id: assignmentDoc.id,
        ...assignmentDoc.data()
      } as YoklaAssignment);
    });

    return assignments;
  } catch (error) {
    console.error('❌ Get all pool assignments error:', error);
    return [];
  }
};

/**
 * Get a single pool assignment by ID
 *
 * @param assignmentId - The assignment ID
 * @returns The pool assignment or null if not found
 */
export const getPoolAssignment = async (assignmentId: string): Promise<YoklaAssignment | null> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);

    if (!assignmentDoc.exists()) {
      return null;
    }

    return {
      id: assignmentDoc.id,
      ...assignmentDoc.data()
    } as YoklaAssignment;
  } catch (error) {
    console.error('❌ Get pool assignment error:', error);
    return null;
  }
};

/**
 * Update pool assignment status
 *
 * @param assignmentId - The assignment ID
 * @param status - New status ('active' | 'completed' | 'passive')
 * @returns true if successful
 */
export const updatePoolAssignmentStatus = async (
  assignmentId: string,
  status: 'active' | 'completed' | 'passive'
): Promise<boolean> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    await updateDoc(assignmentRef, {
      status,
      updatedAt: Timestamp.now()
    });

    console.log(`✅ Updated pool assignment status to ${status}:`, assignmentId);
    return true;
  } catch (error) {
    console.error('❌ Update pool status error:', error);
    return false;
  }
};

/**
 * Check if a teacher is active in a pool assignment
 *
 * @param assignment - The pool assignment
 * @param teacherId - The teacher ID to check
 * @returns true if teacher is active in the pool
 */
export const isTeacherActiveInPool = (assignment: YoklaAssignment, teacherId: string): boolean => {
  if (assignment.assignmentType !== 'pool' || !assignment.poolConfig) {
    return false;
  }

  const teacher = assignment.poolConfig.teachers.find(t => t.id === teacherId);
  return teacher ? teacher.isActive : false;
};

/**
 * Get active teacher count in a pool
 *
 * @param assignment - The pool assignment
 * @returns Number of active teachers
 */
export const getActiveTeacherCount = (assignment: YoklaAssignment): number => {
  if (assignment.assignmentType !== 'pool' || !assignment.poolConfig) {
    return 0;
  }

  return assignment.poolConfig.teachers.filter(t => t.isActive).length;
};

/**
 * Get frozen teacher count in a pool
 *
 * @param assignment - The pool assignment
 * @returns Number of frozen teachers
 */
export const getFrozenTeacherCount = (assignment: YoklaAssignment): number => {
  if (assignment.assignmentType !== 'pool' || !assignment.poolConfig) {
    return 0;
  }

  return assignment.poolConfig.teachers.filter(t => !t.isActive).length;
};
